import { useLingui } from '@lingui/react';
import { CheckCheck } from 'lucide-react';

import { Button } from '@/components/ui/button';

/** What the inbox list does in bulk: approve routine change requests, or acknowledge notices. */
export type WorkflowBulkMode = 'approve' | 'acknowledge';

interface WorkflowSelectionActionsProps {
  mode: WorkflowBulkMode;
  /** Ticked rows, on every page. */
  selectedCount: number;
  /** Ticked rows the action may take; the others are left out. */
  eligibleCount: number;
  busy: boolean;
  onRun: () => void;
  onClear: () => void;
}

/**
 * The actions in the table's selection bar, shown while rows are ticked with
 * the standard checkbox column. A ticked row the action may not take (a change
 * request that needs a person, an accident report) is left out and counted.
 */
export function WorkflowSelectionActions({
  mode,
  selectedCount,
  eligibleCount,
  busy,
  onRun,
  onClear,
}: WorkflowSelectionActionsProps) {
  const { i18n } = useLingui();
  const leftOut = selectedCount - eligibleCount;
  return (
    <>
      <Button size="sm" disabled={eligibleCount === 0 || busy} onClick={onRun} data-testid={`bulk-${mode}-selected`}>
        <CheckCheck className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
        {mode === 'approve'
          ? i18n._({
              id: 'workflow.triage.approveSelectedCount',
              message: 'Approve selected ({count})',
              values: { count: eligibleCount },
            })
          : i18n._({
              id: 'workflow.batchAck.confirmSelected',
              message: 'Acknowledge selected ({count})',
              values: { count: eligibleCount },
            })}
      </Button>
      {leftOut > 0 && (
        <span className="text-xs text-muted-foreground" data-testid="bulk-left-out">
          {mode === 'approve'
            ? i18n._({
                id: 'workflow.bulk.leftOutApprove',
                message:
                  '{count, plural, one {# selected item needs your review and is left out.} other {# selected items need your review and are left out.}}',
                values: { count: leftOut },
              })
            : i18n._({
                id: 'workflow.bulk.leftOutAcknowledge',
                message:
                  '{count, plural, one {# selected item cannot be acknowledged in bulk and is left out.} other {# selected items cannot be acknowledged in bulk and are left out.}}',
                values: { count: leftOut },
              })}
        </span>
      )}
      <Button variant="ghost" size="sm" onClick={onClear} data-testid="bulk-clear-selection">
        {i18n._({ id: 'workflow.bulk.clearSelection', message: 'Clear selection' })}
      </Button>
    </>
  );
}

interface WorkflowBulkAllButtonProps {
  mode: WorkflowBulkMode;
  /** The number of items "Approve all" takes; acknowledge reads every page first, so it has none. */
  count?: number;
  disabled?: boolean;
  onRun: () => void;
}

/** "Approve all (n)" or "Acknowledge all..." in the table toolbar. */
export function WorkflowBulkAllButton({ mode, count, disabled, onRun }: WorkflowBulkAllButtonProps) {
  const { i18n } = useLingui();
  return (
    <Button variant="outline" className="h-10" disabled={disabled} onClick={onRun} data-testid={`bulk-${mode}-all`}>
      {mode === 'approve'
        ? i18n._({
            id: 'workflow.triage.approveAllCount',
            message: 'Approve all ({count})',
            values: { count: count ?? 0 },
          })
        : i18n._({ id: 'workflow.batchAck.all', message: 'Acknowledge all...' })}
    </Button>
  );
}
